/**
 * ルートプラン詳細の画面
 *
 * 当日そのまま開いて使う。地図と常設シートは MapShell が持つので、ここは経路のレイヤーと
 * シートの中身だけを組み立てる。スポットの追加はモード切替で行い、同じ地図に候補のピンを重ねる。
 */

import { Loader2, Plus, RotateCcw, SlidersHorizontal, Sparkles, Trash2, Users } from 'lucide-react'
import { type RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'

import {
  addRoutePlanItems,
  addRoutePlanSpot,
  createPlanStamp,
  deletePlanStamp,
  deleteRoutePlan,
  deleteRoutePlanItem,
  duplicateRoutePlan,
  getMapsSegments,
  getRoutePlan,
  optimizeRoutePlan,
  pinRoutePlanEndpoint,
  reorderRoutePlan,
  restoreRoutePlanOrder,
  splitRoutePlanIntoDays,
  updateRoutePlan,
  updateRoutePlanItem,
} from '@/api/services/route-plan'
import { getStampRallies } from '@/api/services/stamp-rally'
import type {
  AddPlanSpotParams,
  MapsSegment,
  PlanEndpointRole,
  PlanItemRallyLink,
  RoutePlanDetail,
  RoutePlanItem,
  SplitPlanDaysParams,
  UpdateRoutePlanItemParams,
} from '@/api/types/route-plan'
import type { StampRallySummary } from '@/api/types/stamp-rally'
import { RallyFilterBar } from '@/components/shared/map/rally-filter-bar'
import { RallyMapLayer } from '@/components/shared/map/rally-map-layer'
import type { StampFilter } from '@/components/shared/map/stamp-filter'
import { useRallyPlaces } from '@/components/shared/map/use-rally-places'
import { SheetNavHeader } from '@/components/shared/sheet-nav-header'
import type { BottomSheetRef } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import type { ActionSection } from '@/components/ui/custom-dropdown'
import { Skeleton } from '@/components/ui/skeleton'
import type { Coords } from '@/hooks/useCurrentPosition'
import { isSubmitSkipped, useSubmitLock } from '@/hooks/useSubmitLock'
import { apiErrorMessage } from '@/lib/api-error'
import { confirmDialog } from '@/lib/confirm-dialog'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import type { MapScreen, SheetSnap } from '@/pages/map/types'
import { PATHS } from '@/routes/paths'

import type { PlanCondition } from '../lib'
import { addToSelection, buildDepartAt, wantsSplitDays } from '../lib'
import { MapsLaunchButtons } from './components/maps-launch-buttons'
import { PlanAddSpotPanel } from './components/plan-add-spot-panel'
import { PlanConditionPanel } from './components/plan-condition-panel'
import { PlanConditionSummary } from './components/plan-condition-summary'
import { PlanItemPanel } from './components/plan-item-panel'
import { PlanMapLayer } from './components/plan-map-layer'
import { planPoints, routePath } from './components/plan-route-path'
import { PlanShareSheet } from './components/plan-share-sheet'
import { PlanSkippedList } from './components/plan-skipped-list'
import { PlanSplitDaysPanel } from './components/plan-split-days-panel'
import { PlanTimeline } from './components/plan-timeline'
import {
  filterItems,
  isFiltering,
  isTimelineSort,
  legendOf,
  prefecturesOf,
  sortByDistance,
  TIMELINE_SORT_OPTIONS,
  type TimelineSort,
} from './timeline-filter'
import { useDeepOptimizeJob } from './use-deep-optimize-job'

/** シートの中身。地図を隠さないよう、編集も別シートにせずここへ差し替える */
type PlanMode = 'timeline' | 'add' | 'item' | 'condition' | 'days'

const pad = (value: number): string => String(value).padStart(2, '0')

/** UTC の ISO をローカルの「日付」「時刻」へ割る。入力欄がローカル時刻前提のため */
const toLocalParts = (iso: string | null): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' }
  const d = parseUtcDate(iso)
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

const toCondition = (plan: RoutePlanDetail): PlanCondition => {
  const parts = toLocalParts(plan.depart_at)
  return {
    title: plan.title,
    planDate: plan.plan_date ?? parts.date,
    departTime: parts.time,
    travelMode: plan.travel_mode,
    returnToOrigin: plan.return_to_origin,
    defaultStayMinutes: plan.default_stay_minutes,
    maxDurationMinutes: plan.max_duration_minutes,
    originLat: plan.origin_lat,
    originLon: plan.origin_lon,
    originName: plan.origin_name ?? '',
    originPlaceId: plan.origin_place_id,
    destLat: plan.dest_lat,
    destLon: plan.dest_lon,
    destName: plan.dest_name ?? '',
    destPlaceId: plan.dest_place_id,
  }
}

/** 出発地・到着地に充てられる候補。端点に抜いた行も戻せるよう含める */
const endpointCandidates = (plan: RoutePlanDetail): RoutePlanItem[] => {
  const byPlaceId = new Map<number, RoutePlanItem>()
  for (const item of [
    ...(plan.origin_item ? [plan.origin_item] : []),
    ...(plan.dest_item ? [plan.dest_item] : []),
    ...plan.items,
    ...plan.skipped_items,
  ]) {
    if (!byPlaceId.has(item.place_id)) byPlaceId.set(item.place_id, item)
  }
  return [...byPlaceId.values()]
}

type Args = {
  /** false なら別画面が地図を使っている。取得も副作用も止める */
  active: boolean
  planId: number
  coords: Coords | null
  requestPosition: () => Promise<Coords | null>
  sheetRef: RefObject<BottomSheetRef | null>
  snap: SheetSnap
}

export const usePlanDetailScreen = ({ active, planId, coords, requestPosition, sheetRef, snap }: Args): MapScreen => {
  const navigate = useNavigate()

  const [plan, setPlan] = useState<RoutePlanDetail | null>(null)
  const [segments, setSegments] = useState<MapsSegment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { isSubmitting: isOptimizing, run: runOptimize } = useSubmitLock()
  const { isSubmitting: isReordering, run: runReorder } = useSubmitLock()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [mode, setMode] = useState<PlanMode>('timeline')
  const [editingItem, setEditingItem] = useState<RoutePlanItem | null>(null)
  // 候補の取得は追加モードに入ってから。戻っても引き直さないよう戻さない
  const [hasOpenedAdd, setHasOpenedAdd] = useState(false)
  const [rallies, setRallies] = useState<StampRallySummary[]>([])
  const [activeRallyId, setActiveRallyId] = useState<number | null>(null)
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<number[]>([])
  const [activePlaceId, setActivePlaceId] = useState<number | null>(null)
  const { isSubmitting, run } = useSubmitLock()
  const { isSubmitting: isDuplicating, run: runDuplicate } = useSubmitLock()
  // タイムラインの絞り込み。地図の経路はいじらず、行の見え方だけ変える
  const [stampFilter, setStampFilter] = useState<StampFilter>('all')
  const [hiddenRallyIds, setHiddenRallyIds] = useState<number[]>([])
  const [prefectures, setPrefectures] = useState<string[]>([])
  const [sort, setSort] = useState<TimelineSort>('route')
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [allowMultiDay, setAllowMultiDay] = useState(false)

  // 共有されたプランは閲覧とスタンプだけ。編集の導線をまとめてここで落とす
  const readOnly = plan !== null && !plan.is_owner

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!Number.isFinite(planId)) return
      try {
        const detail = await getRoutePlan(planId, signal)
        setPlan(detail)
        setSegments(await getMapsSegments(planId, signal))
      } catch (error) {
        if (signal?.aborted) return
        toast.error(error instanceof Error ? error.message : 'プランの取得に失敗しました')
      } finally {
        if (!signal?.aborted) setIsLoading(false)
      }
    },
    [planId],
  )

  useEffect(() => {
    if (!active) return
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [active, load])

  // 別のプランへ移ったら、前のプランで開いていた編集や選択は意味がないので畳む
  useEffect(() => {
    setMode('timeline')
    setEditingItem(null)
    setSelectedId(null)
    setSelectedPlaceIds([])
    setActivePlaceId(null)
  }, [planId])

  const applyDetail = useCallback(
    async (detail: RoutePlanDetail) => {
      setPlan(detail)
      setSegments(await getMapsSegments(planId))
    },
    [planId],
  )

  const handleOptimize = async () => {
    await runOptimize(async () => {
      try {
        const detail = await optimizeRoutePlan(planId)
        await applyDetail(detail)
        toast.success(detail.skipped_optimize ? '条件が変わっていないため前回の結果を使いました' : '最適化しました')
      } catch (error) {
        toast.error(apiErrorMessage(error, '最適化に失敗しました'))
      }
    })
  }

  const handleRestoreOrder = async () => {
    await runOptimize(async () => {
      try {
        await applyDetail(await restoreRoutePlanOrder(planId))
        toast.success('最適な順番に戻しました')
      } catch (error) {
        toast.error(apiErrorMessage(error, '戻せませんでした'))
      }
    })
  }

  const applyStamps = async (item: RoutePlanItem, links: PlanItemRallyLink[], stamped: boolean) => {
    if (links.length === 0) return
    const stampedAt = stamped ? new Date().toISOString() : null
    const targetIds = new Set(links.map((link) => link.item_id))

    // 現地で押す操作なので往復を待たず見た目を変え、失敗したときだけ読み直して戻す
    const patch = (target: RoutePlanItem | null): RoutePlanItem | null =>
      target !== null && target.place_id === item.place_id
        ? {
            ...target,
            rally_items: target.rally_items.map((link) =>
              targetIds.has(link.item_id) ? { ...link, stamped_at: stampedAt } : link,
            ),
          }
        : target
    setPlan((prev) =>
      prev === null
        ? prev
        : {
            ...prev,
            items: prev.items.map((i) => patch(i) ?? i),
            skipped_items: prev.skipped_items.map((i) => patch(i) ?? i),
            origin_item: patch(prev.origin_item),
            dest_item: patch(prev.dest_item),
          },
    )
    try {
      for (const link of links) {
        if (stamped) await createPlanStamp(planId, item.place_id, link.item_id)
        else await deletePlanStamp(planId, item.place_id, link.item_id)
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'スタンプの更新に失敗しました'))
      await load()
    }
  }

  const handleReorder = async (placeIds: number[]) => {
    // 先に並べ替えを反映しないと、応答が返るまで行が元の位置へ戻ってアニメーションが二重にかかる
    setPlan((prev) => {
      if (prev === null) return prev
      const byPlaceId = new Map(prev.items.map((item) => [item.place_id, item]))
      const reordered = placeIds.map((id) => byPlaceId.get(id)).filter((item) => item !== undefined)
      return reordered.length === prev.items.length ? { ...prev, items: reordered } : prev
    })
    await runReorder(async () => {
      try {
        await applyDetail(await reorderRoutePlan(planId, placeIds))
      } catch {
        toast.error('並べ替えに失敗しました')
        await load()
      }
    })
  }

  const handlePinEndpoint = async (role: PlanEndpointRole, placeId: number) => {
    await runReorder(async () => {
      try {
        await applyDetail(await pinRoutePlanEndpoint(planId, role, placeId))
        toast.success(role === 'origin' ? '出発地にしました' : '到着地にしました')
      } catch (error) {
        toast.error(apiErrorMessage(error, '変更に失敗しました'))
        await load()
      }
    })
  }

  const handleUnpinEndpoint = async (role: PlanEndpointRole, placeIds: number[]) => {
    await runReorder(async () => {
      try {
        // 先に端点から外さないと、並べ替えで端点が前後へ固定し直されてしまう
        await pinRoutePlanEndpoint(planId, role, null)
        await applyDetail(await reorderRoutePlan(planId, placeIds))
        toast.success(role === 'origin' ? '出発地を経由地に移しました' : '到着地を経由地に移しました')
      } catch (error) {
        toast.error(apiErrorMessage(error, '変更に失敗しました'))
        await load()
      }
    })
  }

  const handleRemove = async (item: RoutePlanItem) => {
    await runReorder(async () => {
      try {
        await applyDetail(await deleteRoutePlanItem(planId, item.place_id))
      } catch {
        toast.error('スポットの削除に失敗しました')
      }
    })
  }

  const handleSaveCondition = async (condition: PlanCondition) => {
    try {
      // ピン付けは座標を上書きするので、条件の保存より先に確定させる
      if (plan && condition.originPlaceId !== plan.origin_place_id) {
        await pinRoutePlanEndpoint(planId, 'origin', condition.originPlaceId)
      }
      if (plan && condition.destPlaceId !== plan.dest_place_id) {
        await pinRoutePlanEndpoint(planId, 'dest', condition.destPlaceId)
      }
      await applyDetail(
        await updateRoutePlan(planId, {
          title: condition.title.trim(),
          plan_date: condition.planDate || null,
          depart_at: buildDepartAt(condition.planDate, condition.departTime),
          travel_mode: condition.travelMode,
          return_to_origin: condition.returnToOrigin,
          default_stay_minutes: condition.defaultStayMinutes,
          max_duration_minutes: condition.maxDurationMinutes,
          origin_lat: condition.originLat,
          origin_lon: condition.originLon,
          origin_name: condition.originName.trim() || null,
          dest_lat: condition.destLat,
          dest_lon: condition.destLon,
          dest_name: condition.destName.trim() || null,
        }),
      )
      setMode('timeline')
      toast.success('条件を保存しました')
    } catch (error) {
      toast.error(apiErrorMessage(error, '保存に失敗しました'))
    }
  }

  const handleSaveItem = async (item: RoutePlanItem, params: UpdateRoutePlanItemParams) => {
    try {
      await applyDetail(await updateRoutePlanItem(planId, item.place_id, params))
      backToTimeline()
      // 到着希望は順路そのものに効くので、再最適化しないと見た目に出ない
      const needsReoptimize = 'desired_arrival_at' in params || 'desired_arrival_mode' in params
      toast.success(needsReoptimize ? '保存しました。再最適化で順番に反映されます' : '保存しました')
    } catch (error) {
      toast.error(apiErrorMessage(error, '保存に失敗しました'))
    }
  }

  const handleDelete = async () => {
    if (!(await confirmDialog({ title: 'このプランを削除しますか？', description: plan?.title, destructive: true })))
      return
    try {
      await deleteRoutePlan(planId)
      navigate(PATHS.ROUTE_PLANS.LIST, { replace: true })
    } catch {
      toast.error('削除に失敗しました')
    }
  }

  const handleDuplicate = () =>
    runDuplicate(async () => {
      try {
        const copy = await duplicateRoutePlan(planId)
        toast.success(`「${copy.title}」を作成しました`)
        navigate(PATHS.ROUTE_PLANS.DETAIL.replace(':plan_id', String(copy.id)))
      } catch (error) {
        toast.error(apiErrorMessage(error, '複製に失敗しました'))
      }
    })

  const origin = useMemo<google.maps.LatLngLiteral | null>(
    () =>
      plan?.origin_lat === null || plan?.origin_lat === undefined || plan.origin_lon === null
        ? null
        : { lat: plan.origin_lat, lng: plan.origin_lon },
    [plan],
  )

  const destination = useMemo<google.maps.LatLngLiteral | null>(
    () =>
      plan?.dest_lat === null || plan?.dest_lat === undefined || plan.dest_lon === null
        ? null
        : { lat: plan.dest_lat, lng: plan.dest_lon },
    [plan],
  )

  const openAdd = () => {
    setHasOpenedAdd(true)
    setMode('add')
    // 全高のままだと地図が見えないので、選びに行ける高さまで下げる
    sheetRef.current?.snapTo(snap.half)
  }

  const closeAdd = () => {
    setMode('timeline')
    setSelectedPlaceIds([])
    setActivePlaceId(null)
  }

  const openItem = (item: RoutePlanItem) => {
    setEditingItem(item)
    // 地図のどのピンを見ているのか分かるよう選択も合わせる
    setSelectedId(item.id)
    setMode('item')
    sheetRef.current?.snapTo(snap.full)
  }

  const openCondition = () => {
    setMode('condition')
    sheetRef.current?.snapTo(snap.full)
  }

  const openSplitDays = () => {
    setMode('days')
    sheetRef.current?.snapTo(snap.full)
  }

  // 作成画面で「複数日に分けてもよい」を選んで来た。戻る・再読み込みで開き直さないよう state は消す
  const location = useLocation()
  const hasPlan = plan !== null
  useEffect(() => {
    if (!active || !hasPlan || !wantsSplitDays(location.state)) return
    setAllowMultiDay(true)
    setMode('days')
    sheetRef.current?.snapTo(snap.full)
    navigate(location.pathname, { replace: true, state: null })
  }, [active, hasPlan, location.state, location.pathname, navigate, sheetRef, snap.full])

  const deep = useDeepOptimizeJob({ planId, enabled: active && plan?.is_owner === true, onDone: load })

  const handleSplitDays = async (params: SplitPlanDaysParams, isDeep: boolean) => {
    if (isDeep) {
      try {
        await deep.start(params)
        setMode('timeline')
        toast.success('裏でじっくり組みます。終わったら通知します')
      } catch (error) {
        toast.error(apiErrorMessage(error, 'じっくり最適化を始められませんでした'))
      }
      return
    }
    try {
      const result = await splitRoutePlanIntoDays(planId, params)
      await applyDetail(result)
      setMode('timeline')
      const dayCount = result.day_plans.length
      const rest = result.skipped_count > 0 ? `。${result.skipped_count} 件は入りきりませんでした` : ''
      if (dayCount <= 1) {
        toast.success(`1 日で回れます${rest}`)
        return
      }
      toast.success(`${dayCount} 日に分けました${rest}`, {
        description: result.day_plans.map((day) => day.title).join(' / '),
        action: { label: '一覧へ', onClick: () => navigate(PATHS.ROUTE_PLANS.LIST) },
      })
    } catch (error) {
      toast.error(apiErrorMessage(error, '日分けに失敗しました'))
    }
  }

  const backToTimeline = () => {
    setMode('timeline')
    setEditingItem(null)
  }

  const togglePlace = (placeId: number) =>
    setSelectedPlaceIds((prev) => (prev.includes(placeId) ? prev.filter((id) => id !== placeId) : [...prev, placeId]))

  const selectPlaces = (placeIds: number[]) => setSelectedPlaceIds(addToSelection(selectedPlaceIds, placeIds))

  const handleAddSelected = async () => {
    if (selectedPlaceIds.length === 0) return
    await run(async () => {
      try {
        await applyDetail(await addRoutePlanItems(planId, selectedPlaceIds))
        toast.success(`${selectedPlaceIds.length} 件追加しました`)
        closeAdd()
      } catch (error) {
        toast.error(apiErrorMessage(error, 'スポットの追加に失敗しました'))
      }
    })
  }

  const handleAddSpot = async (spot: AddPlanSpotParams): Promise<boolean> => {
    const result = await run(async () => {
      try {
        await applyDetail(await addRoutePlanSpot(planId, spot))
        toast.success(`${spot.name} を追加しました`)
        closeAdd()
        return true
      } catch (error) {
        toast.error(apiErrorMessage(error, 'スポットの追加に失敗しました'))
        return false
      }
    })
    return !isSubmitSkipped(result) && result
  }

  // 条件パネルは initial を初期値としてしか読まない。無駄な作り直しを避けるためだけの memo
  const condition = useMemo(() => (plan ? toCondition(plan) : null), [plan])

  /** すでにプランへ入っている場所。追加の候補から外す */
  const planPlaceIds = useMemo(
    () =>
      [
        ...(plan?.items ?? []),
        ...(plan?.skipped_items ?? []),
        ...(plan?.origin_item ? [plan.origin_item] : []),
        ...(plan?.dest_item ? [plan.dest_item] : []),
      ].map((item) => item.place_id),
    [plan],
  )

  const spotCandidates = useMemo(() => (plan ? endpointCandidates(plan) : []), [plan])

  const planItems = useMemo(() => plan?.items ?? [], [plan])
  const skippedItems = useMemo(() => plan?.skipped_items ?? [], [plan])
  const timelineFilters = useMemo(
    () => ({ stampFilter, hiddenRallyIds, prefectures }),
    [stampFilter, hiddenRallyIds, prefectures],
  )
  const timelineLegend = useMemo(() => legendOf(planItems), [planItems])
  const timelinePrefectures = useMemo(() => prefecturesOf(planItems), [planItems])
  const timelineItems = useMemo(() => {
    const filtered = filterItems(planItems, timelineFilters)
    return sort === 'distance' ? sortByDistance(filtered, coords) : filtered
  }, [planItems, timelineFilters, sort, coords])
  // 経路が崩れるので、絞り込みか距離順のあいだは並べ替えを止めて番号もルートの位置を出す
  const isTimelineFiltered = isFiltering(timelineFilters) || sort !== 'route'
  const routeNumberOf = useMemo(() => {
    const byId = new Map(planItems.map((item, index) => [item.id, index + 1]))
    return (item: RoutePlanItem) => byId.get(item.id) ?? 0
  }, [planItems])
  const mutedIds = useMemo(
    () => planItems.filter((item) => !timelineItems.includes(item)).map((item) => item.id),
    [planItems, timelineItems],
  )

  const handleChangeSort = async (next: TimelineSort) => {
    if (next !== 'distance' || coords) {
      setSort(next)
      return
    }
    if (!(await requestPosition())) {
      toast.error('現在地を取得できませんでした')
      return
    }
    setSort(next)
  }

  const rallyPlaces = useRallyPlaces({
    rallies,
    excludePlaceIds: planPlaceIds,
    enabled: active && hasOpenedAdd,
    focusRallyId: activeRallyId,
    keepPlaceId: activePlaceId,
    currentPlanId: plan?.id ?? null,
  })

  useEffect(() => {
    if (!active || !hasOpenedAdd) return
    const controller = new AbortController()
    getStampRallies(undefined, controller.signal)
      .then(setRallies)
      .catch(() => {
        if (!controller.signal.aborted) toast.error('ラリーの取得に失敗しました')
      })
    return () => controller.abort()
  }, [active, hasOpenedAdd])

  // 旅の記録は自動で紐付くので、あるときだけ出す
  const menuSections: ActionSection[] = [
    {
      type: 'action',
      label: '移動',
      items: [
        ...(plan?.trip_id
          ? [
              {
                key: 'trip',
                label: plan.trip_title ?? '旅の記録を開く',
                onClick: () => navigate(PATHS.TRIPS.DETAIL.replace(':trip_id', String(plan.trip_id))),
              },
            ]
          : []),
        { key: 'plans', label: 'ルート一覧', onClick: () => navigate(PATHS.ROUTE_PLANS.LIST) },
        { key: 'rallies', label: 'スタンプラリー一覧', onClick: () => navigate(PATHS.RALLIES.LIST) },
      ],
    },
    ...(readOnly
      ? []
      : [
          {
            type: 'action' as const,
            label: 'プラン',
            items: [
              {
                key: 'duplicate',
                label: isDuplicating ? '複製中…' : 'プランを複製',
                onClick: () => void handleDuplicate(),
              },
              ...(deep.isActive
                ? []
                : [{ key: 'split-days', label: '日帰りで何日かに分ける', onClick: openSplitDays }]),
            ],
          },
          {
            type: 'action' as const,
            label: '共有',
            items: [{ key: 'share', label: '共有設定', onClick: () => setIsShareOpen(true) }],
          },
        ]),
  ]

  const sheetHeader = (() => {
    if (mode === 'add') {
      return (
        <SheetNavHeader
          onBack={closeAdd}
          title="地図かリストから選ぶ"
          action={
            <Button
              size="sm"
              className="shrink-0 px-3!"
              disabled={selectedPlaceIds.length === 0 || isSubmitting}
              onClick={() => void handleAddSelected()}
            >
              {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : `${selectedPlaceIds.length} 件を追加`}
            </Button>
          }
        />
      )
    }
    if (mode === 'item') {
      return <SheetNavHeader onBack={backToTimeline} title={editingItem?.name ?? 'スポット'} />
    }
    if (mode === 'condition') {
      return <SheetNavHeader onBack={backToTimeline} title="条件を編集" />
    }
    if (mode === 'days') {
      return <SheetNavHeader onBack={backToTimeline} title="日帰りで何日かに分ける" />
    }
    return (
      <SheetNavHeader
        onBack={() => navigate(PATHS.ROUTE_PLANS.LIST)}
        title={
          <span className="tabular-nums text-muted-foreground">
            {plan ? `${plan.items.length} 件のスポット` : '読み込み中'}
          </span>
        }
        action={
          readOnly ? undefined : (
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="outline" className="gap-1.5 px-3!" onClick={openCondition}>
                <SlidersHorizontal size={15} />
                編集
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 px-3!" onClick={openAdd}>
                <Plus size={15} />
                追加
              </Button>
            </div>
          )
        }
      />
    )
  })()

  const filterBar = (() => {
    if (mode === 'add') {
      const unselectedVisible = rallyPlaces.visible
        .map(({ place }) => place.placeId)
        .filter((id) => !selectedPlaceIds.includes(id))
      return (
        <RallyFilterBar
          count={rallyPlaces.visible.length}
          selectedCount={selectedPlaceIds.length}
          onSelectAll={unselectedVisible.length > 0 ? () => selectPlaces(unselectedVisible) : undefined}
          stampFilter={rallyPlaces.stampFilter}
          onStampFilterChange={rallyPlaces.setStampFilter}
          legend={activeRallyId === null ? rallyPlaces.legend : []}
          hiddenRallyIds={rallyPlaces.hiddenRallyIds}
          onToggleRally={rallyPlaces.toggleRally}
          onToggleAllRallies={rallyPlaces.setAllRalliesHidden}
          planLegend={rallyPlaces.planLegend}
          hiddenPlanIds={rallyPlaces.hiddenPlanIds}
          onTogglePlan={rallyPlaces.togglePlan}
          onToggleAllPlans={rallyPlaces.setAllPlansHidden}
        />
      )
    }
    if (mode !== 'timeline' || planItems.length === 0) return undefined
    return (
      <RallyFilterBar
        count={timelineItems.length}
        selectedCount={0}
        stampFilter={stampFilter}
        onStampFilterChange={setStampFilter}
        legend={timelineLegend}
        hiddenRallyIds={hiddenRallyIds}
        onToggleRally={(rallyId) =>
          setHiddenRallyIds((prev) =>
            prev.includes(rallyId) ? prev.filter((id) => id !== rallyId) : [...prev, rallyId],
          )
        }
        onToggleAllRallies={(hidden) => setHiddenRallyIds(hidden ? timelineLegend.map(({ rally }) => rally.id) : [])}
        prefectures={timelinePrefectures}
        selectedPrefectures={prefectures}
        onTogglePrefecture={(value) =>
          setPrefectures((prev) => (prev.includes(value) ? prev.filter((name) => name !== value) : [...prev, value]))
        }
        onClearPrefectures={() => setPrefectures([])}
        sortOptions={TIMELINE_SORT_OPTIONS}
        sortValue={sort}
        onSortChange={(value) => isTimelineSort(value) && void handleChangeSort(value)}
      />
    )
  })()

  const planFitPoints = useMemo(
    () => [
      ...routePath(planPoints(planItems, []), origin, plan?.return_to_origin ?? true, destination),
      ...planPoints(skippedItems, []),
    ],
    [planItems, skippedItems, origin, destination, plan?.return_to_origin],
  )

  return {
    title: plan?.title ?? 'ルートプラン',
    menuSections,
    filterBar,
    mapLayer: plan && (
      <>
        <PlanMapLayer
          items={plan.items}
          skippedItems={plan.skipped_items}
          mutedIds={mode === 'timeline' ? mutedIds : []}
          origin={origin}
          returnToOrigin={plan.return_to_origin}
          destination={destination}
          routePolyline={plan.route_polyline}
          selectedId={selectedId}
          onSelect={(item) => setSelectedId(item.id)}
        />

        {mode === 'add' && (
          <RallyMapLayer
            rallyPlaces={rallyPlaces}
            activePlaceId={activePlaceId}
            onActivate={setActivePlaceId}
            selectedPlaceIds={selectedPlaceIds}
            onToggle={(place) => togglePlace(place.placeId)}
          />
        )}
      </>
    ),
    onMapClick: () => setActivePlaceId(null),
    fitPoints: mode === 'add' ? rallyPlaces.points : planFitPoints,
    fitKey: mode === 'add' ? `add-${rallyPlaces.fitKey}` : `plan-${planId}-${planFitPoints.length}`,
    sheetHeader,
    sheetBody: (
      <>
        <div className={cn(mode !== 'timeline' && 'hidden')}>
          {isLoading && !plan ? (
            <div className="space-y-3 p-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : !plan ? null : (
            <>
              {plan.owner_name && (
                <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  <Users size={13} className="shrink-0" />
                  {plan.owner_name} さんから共有されたプランです。閲覧とスタンプのみ行えます
                </div>
              )}

              <PlanConditionSummary plan={plan} />

              {plan.items.length === 0 &&
              plan.skipped_items.length === 0 &&
              plan.origin_item === null &&
              plan.dest_item === null ? (
                <div className="flex flex-col items-center gap-3 py-16">
                  <p className="text-sm text-muted-foreground">スポットがまだありません</p>
                  {!readOnly && (
                    <Button variant="outline" className="gap-2" onClick={openAdd}>
                      <Plus size={16} />
                      場所を追加
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {readOnly ? null : plan.optimize_hash === null || deep.isActive ? (
                    <div className="border-b bg-muted/30 p-3">
                      {!deep.isActive && (
                        <label className="mb-2.5 flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={allowMultiDay}
                            onCheckedChange={(checked) => setAllowMultiDay(checked === true)}
                          />
                          複数日（日帰り × N 日）に分けてもよい
                        </label>
                      )}
                      <Button
                        className="w-full gap-2"
                        // 裏の結果があとから並びと日分けを上書きするので、終わるまで次の最適化は走らせない
                        disabled={isOptimizing || deep.isActive}
                        onClick={() => (allowMultiDay ? openSplitDays() : void handleOptimize())}
                      >
                        {isOptimizing || deep.isActive ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Sparkles size={16} />
                        )}
                        {deep.isActive
                          ? `じっくり最適化中（${deep.finishLabel} ごろまで）`
                          : allowMultiDay
                            ? '候補日を選んで最適化する'
                            : plan.status === 'draft'
                              ? '訪問順を最適化する'
                              : '変更を反映して再最適化する'}
                      </Button>
                      <p className="mt-2 text-center text-xs text-muted-foreground">
                        {deep.isActive ? (
                          '終わったら日ごとのプランに分けて通知します'
                        ) : (
                          <>
                            {plan.status !== 'draft' && '条件かスポットが変わっています。'}
                            スポット数ぶん Google の枠を消費します（{plan.items.length} 件）
                          </>
                        )}
                      </p>
                    </div>
                  ) : (
                    plan.is_reordered && (
                      <div className="border-b bg-muted/30 p-3">
                        <Button
                          variant="outline"
                          className="w-full gap-2"
                          disabled={isOptimizing || deep.isActive}
                          onClick={() => void handleRestoreOrder()}
                        >
                          {isOptimizing ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                          最適な順番に戻す
                        </Button>
                        <p className="mt-2 text-center text-xs text-muted-foreground">
                          手で並べ替えています。保存済みの最適化結果に戻すだけなので Google の枠は使いません
                        </p>
                      </div>
                    )
                  )}

                  <MapsLaunchButtons segments={segments} className="border-b" />

                  <PlanTimeline
                    items={timelineItems}
                    numberOf={routeNumberOf}
                    isFiltered={isTimelineFiltered}
                    originName={plan.origin_name}
                    returnToOrigin={plan.return_to_origin}
                    destName={plan.dest_name}
                    originItem={plan.origin_item}
                    destItem={plan.dest_item}
                    departAt={plan.depart_at}
                    totalDurationSec={plan.total_duration_sec}
                    totalDistanceM={plan.total_distance_m}
                    selectedId={selectedId}
                    isReordering={isReordering}
                    readOnly={readOnly}
                    onSelect={(item) => setSelectedId(item.id)}
                    onEdit={openItem}
                    onToggleStamp={(item, links, stamped) => void applyStamps(item, links, stamped)}
                    onReorder={(placeIds) => void handleReorder(placeIds)}
                    onPinEndpoint={(role, placeId) => void handlePinEndpoint(role, placeId)}
                    onUnpinEndpoint={(role, placeIds) => void handleUnpinEndpoint(role, placeIds)}
                    onRemove={(item) => void handleRemove(item)}
                  />

                  <PlanSkippedList
                    items={plan.skipped_items}
                    isBusy={isReordering}
                    readOnly={readOnly}
                    onEdit={openItem}
                    onRemove={(item) => void handleRemove(item)}
                    onSplitDays={deep.isActive ? undefined : openSplitDays}
                  />

                  <p className="px-3 text-xs text-muted-foreground">
                    営業時間・臨時休業は参考値です
                    {plan.opening_hours_fetched_at &&
                      `（${parseUtcDate(plan.opening_hours_fetched_at).toLocaleDateString('ja-JP')} 時点）`}
                    。特別営業日やその後の再開は反映されないことがあります
                  </p>
                </>
              )}

              {!readOnly && (
                <div className="px-3 pt-6 pb-6">
                  <Button variant="outline" className="w-full text-destructive" onClick={() => void handleDelete()}>
                    <Trash2 size={16} />
                    プランを削除
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {hasOpenedAdd && (
          <div className={cn(mode !== 'add' && 'hidden')}>
            <PlanAddSpotPanel
              origin={origin && { latitude: origin.lat, longitude: origin.lng }}
              excludePlaceIds={planPlaceIds}
              rallies={rallies}
              rallyId={activeRallyId}
              onSelectRally={setActiveRallyId}
              selectedPlaceIds={selectedPlaceIds}
              onTogglePlace={togglePlace}
              onSelectPlaces={selectPlaces}
              onAddSpot={handleAddSpot}
              isSubmitting={isSubmitting}
            />
          </div>
        )}

        {mode === 'item' && plan && editingItem && (
          <PlanItemPanel
            key={editingItem.place_id}
            item={editingItem}
            planDate={plan.plan_date ?? toLocalParts(plan.depart_at).date}
            isPinned={editingItem.place_id === plan.origin_place_id || editingItem.place_id === plan.dest_place_id}
            onSubmit={handleSaveItem}
          />
        )}

        {mode === 'condition' && condition && (
          <PlanConditionPanel initial={condition} spots={spotCandidates} onSubmit={handleSaveCondition} />
        )}

        {mode === 'days' && plan && condition && (
          <PlanSplitDaysPanel
            initialDate={condition.planDate || toLocalParts(new Date().toISOString()).date}
            initialMaxMinutes={plan.max_duration_minutes}
            spotCount={plan.items.length + plan.skipped_items.length}
            dayConditionLabel={`${plan.origin_name || '出発地'}を${condition.departTime || '出発時刻'}に出る`}
            onSubmit={handleSplitDays}
            isDeepRunning={deep.isActive}
          />
        )}
      </>
    ),
    dialogs: <PlanShareSheet planIds={[planId]} open={isShareOpen} onOpenChange={setIsShareOpen} />,
  }
}
